import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {httpGetUsers, HttpGetUsersOptions} from '@/features/users/http/users.http.ts'
import {captureException} from '@sentry/react'
import {UserSmall} from '@/features/users/types.ts'
import {z} from 'zod'
import {PaginationResponse} from '@/types.ts'
import {useEffect, useState} from 'react'

const ResponseShape = z.object({data: z.array(UserSmall), meta: PaginationResponse})
type ResponseShape = z.infer<typeof ResponseShape>

export const useGetUsers = (
    params: HttpGetUsersOptions['params'],
    payload?: HttpGetUsersOptions['payload'],
    options?: Omit<UseQueryOptions<ResponseShape>, 'queryKey' | 'queryFn'>
) => {
    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: 50,
        currentPage: 1
    })

    const query = useQuery({
        queryKey: [QUERY_KEYS.USERS, params, payload],
        queryFn: () =>
            parseAxiosPromise({
                axiosPromise: httpGetUsers({params, payload}),
                onZodError: captureException,
                responseShape: ResponseShape
            }),
        ...options
    })

    useEffect(() => {
        if (query.isSuccess) {
            setPersistedPagination(query.data.meta)
        }
    }, [query.data?.data.length])

    return {...query, persistedPagination}
}
